// app/filter-clause.ts
//
// Pure functions for FilterClause: clause building, chip formatting and
// client-side row matching. No React, no @tanstack/react-table.
//
// These functions are shared by FilterBar, TableFilters, and SmartTable.
// They contain NO React state and NO UI logic — only domain rules.
//
// NULL semantics for applyFilterClauseToRow MUST match the server-side
// buildFilterSql (see ../infra/drizzle/build-filter-sql.ts):
//   For negate:true on nullable columns (text/select): NULL rows satisfy
//   "not equals / not in" (i.e., they are INCLUDED).
//   For empty with negate: behaves as not-empty. For not-empty with negate:
//   behaves as empty.

import type {
  FilterClause,
  SelectList,
  SelectListItem,
  TextOperator,
} from "../domain";
import { FILTER_TYPE_OBJ, TEXT_OPERATOR_OBJ } from "../domain";
import {
  DEFAULT_TABLE_LABELS_EN,
  type TableLabelKey,
  type TableLabels,
} from "./table-labels";

/** Язык подписей списков по умолчанию. */
export const DEFAULT_LOCALE = "en-US";

// ── Operator symbols (single source of truth) ──────────────────────────────

export const OPERATOR_SYMBOLS: Record<TextOperator, string> = {
  contains: "~",
  equals: "=",
  starts: "^",
  ends: "$",
  "not-contains": "!~",
  "not-equals": "!=",
};

/** Ключи `TableLabels` для названий текстовых операторов. */
export const OPERATOR_LABEL_KEYS: Record<TextOperator, TableLabelKey> = {
  contains: "operatorContains",
  equals: "operatorEquals",
  starts: "operatorStarts",
  ends: "operatorEnds",
  "not-contains": "operatorNotContains",
  "not-equals": "operatorNotEquals",
};

/** Английские названия операторов (значения по умолчанию). */
export const OPERATOR_LABELS: Record<TextOperator, string> = {
  contains: DEFAULT_TABLE_LABELS_EN.operatorContains,
  equals: DEFAULT_TABLE_LABELS_EN.operatorEquals,
  starts: DEFAULT_TABLE_LABELS_EN.operatorStarts,
  ends: DEFAULT_TABLE_LABELS_EN.operatorEnds,
  "not-contains": DEFAULT_TABLE_LABELS_EN.operatorNotContains,
  "not-equals": DEFAULT_TABLE_LABELS_EN.operatorNotEquals,
};

// ── Select filter operators ─────────────────────────────────────────────────
//
// Unified operator model for list-type filters (select-single/select-multi).
// Negation is expressed through the operator (not-equals, not-in, is-not-empty),
// NOT through a separate global NOT checkbox — this eliminates double negation.

export const SELECT_OPERATORS = [
  "equals",
  "not-equals",
  "in",
  "not-in",
  "is-empty",
  "is-not-empty",
] as const;
export type SelectFilterOperator = (typeof SELECT_OPERATORS)[number];
export const SELECT_OPERATOR_OBJ = {
  EQUALS: "equals",
  NOT_EQUALS: "not-equals",
  IN: "in",
  NOT_IN: "not-in",
  IS_EMPTY: "is-empty",
  IS_NOT_EMPTY: "is-not-empty",
} as const satisfies Record<string, SelectFilterOperator>;

/** Label keys for select operators (resolved through TableLabels in UI). */
export const SELECT_OPERATOR_I18N_KEYS = {
  equals: "table.filters.operator.equals",
  "not-equals": "table.filters.operator.notEquals",
  in: "table.filters.operator.in",
  "not-in": "table.filters.operator.notIn",
  "is-empty": "table.filters.operator.isEmpty",
  "is-not-empty": "table.filters.operator.isNotEmpty",
} as const satisfies Record<SelectFilterOperator, TableLabelKey>;

/**
 * Convert a SelectFilterOperator + values to a FilterClause.
 * This is the single source of truth for how operators map to clauses.
 */
export function operatorToClause(
  column: string,
  operator: SelectFilterOperator,
  singleValue: string,
  multiValues: string[],
): FilterClause | null {
  switch (operator) {
    case TEXT_OPERATOR_OBJ.EQUALS:
      if (!singleValue) return null;
      return { column, type: "select-single", value: singleValue };
    case TEXT_OPERATOR_OBJ.NOT_EQUALS:
      if (!singleValue) return null;
      return {
        column,
        type: "select-single",
        value: singleValue,
        negate: true,
      };
    case SELECT_OPERATOR_OBJ.IN:
      if (multiValues.length === 0) return null;
      return { column, type: "select-multi", values: multiValues };
    case "not-in":
      if (multiValues.length === 0) return null;
      return {
        column,
        type: "select-multi",
        values: multiValues,
        negate: true,
      };
    case "is-empty":
      return { column, type: "empty" };
    case "is-not-empty":
      return { column, type: "not-empty" };
  }
}

/**
 * Infer the SelectFilterOperator from an existing FilterClause.
 * Used when loading presets/URL state for editing.
 */
export function clauseToOperator(
  clause: FilterClause,
): SelectFilterOperator | null {
  switch (clause.type) {
    case FILTER_TYPE_OBJ.SELECT_SINGLE:
      return clause.negate ? "not-equals" : "equals";
    case FILTER_TYPE_OBJ.SELECT_MULTI:
      return clause.negate ? "not-in" : "in";
    case FILTER_TYPE_OBJ.EMPTY:
      return "is-empty";
    case FILTER_TYPE_OBJ.NOT_EMPTY:
      return "is-not-empty";
    default:
      return null;
  }
}

// ── List labels ─────────────────────────────────────────────────────────────

export function getListItemLabel(
  items: SelectListItem[] | undefined,
  value: string,
  locale: string = DEFAULT_LOCALE,
): string | undefined {
  const item = items?.find((i) => i.value === value);
  const label = item?.label;
  if (!label) return undefined;
  return label[locale] ?? label[Object.keys(label)[0] ?? ""] ?? value;
}

// ── Clause building ─────────────────────────────────────────────────────────

/**
 * Input for building a FilterClause from draft state.
 * All fields are optional/plain — no React state required.
 */
export interface FilterDraftInput {
  column: string;
  filterType: FilterClause["type"] | null;
  draftValue: string;
  draftOperator: TextOperator;
  draftMin: string;
  draftMax: string;
  draftFrom: string;
  draftTo: string;
  draftSelect: string;
  draftBool: boolean;
  draftMultiValues: string[];
  draftNegate: boolean;
}

/**
 * Build a FilterClause from draft input.
 * Returns null if the draft is empty/invalid (no value to filter on).
 */
export function buildFilterClause(
  input: FilterDraftInput,
): FilterClause | null {
  const { column, filterType, draftNegate } = input;
  if (!filterType) return null;
  const negate = draftNegate ? { negate: true } : {};
  switch (filterType) {
    case FILTER_TYPE_OBJ.TEXT:
      if (!input.draftValue.trim()) return null;
      return {
        column,
        type: "text",
        value: input.draftValue,
        operator: input.draftOperator,
        ...negate,
      };
    case FILTER_TYPE_OBJ.NUMBER_RANGE: {
      const min = input.draftMin ? Number(input.draftMin) : undefined;
      const max = input.draftMax ? Number(input.draftMax) : undefined;
      if (
        (min === undefined || !Number.isFinite(min)) &&
        (max === undefined || !Number.isFinite(max))
      ) {
        return null;
      }
      return {
        column,
        type: "number-range",
        ...(Number.isFinite(min) ? { min } : {}),
        ...(Number.isFinite(max) ? { max } : {}),
        ...negate,
      };
    }
    case FILTER_TYPE_OBJ.DATE_RANGE: {
      const hasFrom = input.draftFrom !== "";
      const hasTo = input.draftTo !== "";
      if (!hasFrom && !hasTo) return null;
      return {
        column,
        type: "date-range",
        ...(hasFrom ? { from: input.draftFrom } : {}),
        ...(hasTo ? { to: input.draftTo } : {}),
        ...negate,
      };
    }
    case FILTER_TYPE_OBJ.SELECT_SINGLE:
      if (!input.draftSelect) return null;
      return {
        column,
        type: "select-single",
        value: input.draftSelect,
        ...negate,
      };
    case FILTER_TYPE_OBJ.SELECT_MULTI:
      if (input.draftMultiValues.length === 0) return null;
      return {
        column,
        type: "select-multi",
        values: input.draftMultiValues,
        ...negate,
      };
    case FILTER_TYPE_OBJ.BOOLEAN:
      return {
        column,
        type: "boolean",
        value: input.draftBool,
        ...negate,
      };
    case FILTER_TYPE_OBJ.EMPTY:
      return { column, type: "empty", ...negate };
    case FILTER_TYPE_OBJ.NOT_EMPTY:
      return { column, type: "not-empty", ...negate };
    default:
      return null;
  }
}

// ── Chip formatting ─────────────────────────────────────────────────────────

/**
 * Format a FilterClause as a human-readable chip label.
 * Single source of truth — replaces formatFilterChip and formatChipLabel.
 */
export function formatFilterClauseLabel(
  clause: FilterClause,
  lists?: SelectList[],
  locale: string = DEFAULT_LOCALE,
  labels?: Pick<TableLabels, "chipIsEmpty" | "chipIsNotEmpty">,
): string {
  const neg = clause.negate ? "≠" : "";
  const col = clause.column;
  const listItems = lists?.find((l) => l.propertyKey === col)?.items;
  const label = (value: string): string =>
    getListItemLabel(listItems, value, locale) ?? value;
  const isEmpty = labels?.chipIsEmpty ?? DEFAULT_TABLE_LABELS_EN.chipIsEmpty;
  const isNotEmpty =
    labels?.chipIsNotEmpty ?? DEFAULT_TABLE_LABELS_EN.chipIsNotEmpty;
  switch (clause.type) {
    case FILTER_TYPE_OBJ.TEXT: {
      const op = clause.operator ?? "contains";
      return `${col} ${neg}${OPERATOR_SYMBOLS[op] ?? "~"} ${clause.value}`;
    }
    case FILTER_TYPE_OBJ.SELECT_SINGLE:
      return `${col} ${neg}= ${label(clause.value)}`;
    case FILTER_TYPE_OBJ.SELECT_MULTI:
      return `${col}: ${clause.values.map(label).join(", ")}`;
    case FILTER_TYPE_OBJ.BOOLEAN:
      return `${col} ${neg}= ${clause.value}`;
    case FILTER_TYPE_OBJ.NUMBER_RANGE: {
      const parts: string[] = [];
      if (clause.min !== undefined) parts.push(String(clause.min));
      if (clause.max !== undefined) parts.push(String(clause.max));
      return `${col}: ${parts.join("–")}`;
    }
    case FILTER_TYPE_OBJ.DATE_RANGE: {
      const parts: string[] = [];
      if (clause.from !== undefined) parts.push(clause.from);
      if (clause.to !== undefined) parts.push(clause.to);
      return `${col}: ${parts.join("–")}`;
    }
    case FILTER_TYPE_OBJ.EMPTY:
      return `${col} ${neg}${isEmpty}`;
    case FILTER_TYPE_OBJ.NOT_EMPTY:
      return `${col} ${neg}${isNotEmpty}`;
  }
}

// ── Client-side row matching ────────────────────────────────────────────────

/**
 * Apply a FilterClause to a row for client-side filtering.
 *
 * NULL semantics MUST match the server-side buildFilterSql:
 *   For negate:true on text/select-single/select-multi: NULL rows are
 *   INCLUDED (they satisfy "not equals / not in").
 *   For empty with negate → behaves as not-empty.
 *   For not-empty with negate → behaves as empty.
 */
export function applyFilterClauseToRow(
  row: unknown,
  clause: FilterClause,
): boolean {
  const get = (obj: unknown, path: string): unknown =>
    path.split(".").reduce<unknown>((acc, key) => {
      if (
        typeof acc === "object" &&
        acc !== null &&
        !Array.isArray(acc) &&
        key in acc
      )
        return Reflect.get(acc, key);
      return undefined;
    }, obj);
  const v = get(row, clause.column);
  const isNull = v === null || v === undefined;

  // Handle empty/not-empty with negate by swapping
  if (clause.type === FILTER_TYPE_OBJ.EMPTY) {
    const isEmpty = isNull || v === "";
    return clause.negate ? !isEmpty : isEmpty;
  }
  if (clause.type === FILTER_TYPE_OBJ.NOT_EMPTY) {
    const isNotEmpty = !isNull && v !== "";
    return clause.negate ? !isNotEmpty : isNotEmpty;
  }

  let result: boolean;
  switch (clause.type) {
    case FILTER_TYPE_OBJ.TEXT: {
      const sv = !isNull ? String(v).toLowerCase() : "";
      const cv = String(clause.value).toLowerCase();
      const op = clause.operator ?? "contains";
      // SQL three-valued logic: NOT (col ilike '%x%') on NULL → UNKNOWN → false.
      // Client must match: negative text operators on NULL → false (without negate).
      if (
        isNull &&
        (op === TEXT_OPERATOR_OBJ.NOT_CONTAINS ||
          op === TEXT_OPERATOR_OBJ.NOT_EQUALS)
      ) {
        result = false;
        break;
      }
      switch (op) {
        case TEXT_OPERATOR_OBJ.CONTAINS:
          result = sv.includes(cv);
          break;
        case TEXT_OPERATOR_OBJ.EQUALS:
          result = sv === cv;
          break;
        case TEXT_OPERATOR_OBJ.STARTS:
          result = sv.startsWith(cv);
          break;
        case TEXT_OPERATOR_OBJ.ENDS:
          result = sv.endsWith(cv);
          break;
        case TEXT_OPERATOR_OBJ.NOT_CONTAINS:
          result = !sv.includes(cv);
          break;
        case TEXT_OPERATOR_OBJ.NOT_EQUALS:
          result = sv !== cv;
          break;
        default:
          result = sv.includes(cv);
      }
      break;
    }
    case FILTER_TYPE_OBJ.SELECT_SINGLE:
      result = v === clause.value;
      break;
    case FILTER_TYPE_OBJ.SELECT_MULTI:
      result = typeof v === "string" && clause.values.includes(v);
      break;
    case FILTER_TYPE_OBJ.BOOLEAN:
      result = v === clause.value;
      break;
    case FILTER_TYPE_OBJ.NUMBER_RANGE: {
      const n = typeof v === "number" ? v : Number(v);
      if (!Number.isFinite(n)) {
        result = false;
        break;
      }
      if (clause.min !== undefined && n < clause.min) {
        result = false;
        break;
      }
      if (clause.max !== undefined && n > clause.max) {
        result = false;
        break;
      }
      result = true;
      break;
    }
    case FILTER_TYPE_OBJ.DATE_RANGE: {
      const d = v instanceof Date ? v : new Date(String(v));
      if (Number.isNaN(d.getTime())) {
        result = false;
        break;
      }
      if (clause.from !== undefined && d < new Date(clause.from)) {
        result = false;
        break;
      }
      if (clause.to !== undefined && d > new Date(clause.to)) {
        result = false;
        break;
      }
      result = true;
      break;
    }
    default:
      result = true;
  }

  // Apply negation with NULL semantics matching server-side buildFilterSql.
  // For text/select on nullable columns: negate INCLUDES NULL rows.
  if (clause.negate) {
    if (
      isNull &&
      (clause.type === FILTER_TYPE_OBJ.TEXT ||
        clause.type === FILTER_TYPE_OBJ.SELECT_SINGLE ||
        clause.type === FILTER_TYPE_OBJ.SELECT_MULTI)
    ) {
      return true; // NULL satisfies "not equals / not in"
    }
    // For number-range/date-range/boolean: NULL → SQL UNKNOWN → NOT (UNKNOWN) → UNKNOWN → false.
    // Client must match: NULL rows are excluded under negate for these types.
    if (
      isNull &&
      (clause.type === FILTER_TYPE_OBJ.NUMBER_RANGE ||
        clause.type === FILTER_TYPE_OBJ.DATE_RANGE ||
        clause.type === FILTER_TYPE_OBJ.BOOLEAN)
    ) {
      return false;
    }
    return !result;
  }
  return result;
}
